import { Color, InstancedBufferAttribute, InstancedMesh, Matrix4 } from 'three';
import type { BufferGeometry, Material } from 'three';
import type { LinearRGB } from '../../core/color';
import type { Rng } from '../../core/rng';
import type { PaletteModel } from '../../data/types';
import { sampleSwatch } from '../../data/palette';
import { edgeIdFor } from '../mesh/attributes';
import { layoutToWorld } from '../../core/coords';
import type { Vec2 } from '../../geo/types';
import type { GroundPiece } from '../../site/types';
import {
  buildCrownTemplate,
  buildTrunkTemplate,
  placeOrchardTrees,
  placeGardenTrees,
  placeCloisterTrees,
  CROWN_CENTRE_ABOVE_TRUNK,
  CROWN_VARIANTS,
  type IsBlocked,
  type TreePlacement,
} from './trees';

export type { TreePlacement } from './trees';

export interface VegetationCounts {
  readonly trees: number;
  readonly orchardTrees: number;
  readonly gardenTrees: number;
  readonly cloisterTrees: number;
  readonly farmTrees: number;
}

export interface TreeMeshes {
  readonly meshes: readonly InstancedMesh[];
  readonly trunks: InstancedMesh | null;
  readonly crowns: readonly InstancedMesh[];
  readonly treeCount: number;
}

export interface CollectedTrees {
  readonly orchards: readonly TreePlacement[];
  readonly gardens: readonly TreePlacement[];
  readonly cloisters: readonly TreePlacement[];
}

/* A garden of kind yard is a timber yard, which the painting fills with timber, not trees. */
const PLACERS = {
  orchard: placeOrchardTrees,
  garden: placeGardenTrees,
  cloister: placeCloisterTrees,
} as const;

type TreeKind = keyof typeof PLACERS;

function isTreeKind(kind: string): kind is TreeKind {
  return kind in PLACERS;
}

/* A tree's foliage is drawn for where it stands, not for its place in the list, which any tree
   planted ahead of it would shift. */
function treeName(tree: TreePlacement): string {
  return tree.position.map((c) => c.toFixed(3)).join(',');
}

export function collectAllTrees(
  ground: readonly GroundPiece[],
  isBlocked: IsBlocked,
  rng: Rng
): CollectedTrees {
  const byKind: Record<TreeKind, TreePlacement[]> = {
    orchard: [],
    garden: [],
    cloister: [],
  };
  for (const piece of ground) {
    const kind = piece.kind;
    if (isTreeKind(kind)) {
      // All four Raamsteeg plots are explicitly tree-filled in both Anthonisz views.
      const trees =
        kind === 'garden' && piece.regionId.startsWith('garden-raamsteeg-')
          ? placeGardenTrees(piece, isBlocked, rng.fork(piece.key), 1)
          : PLACERS[kind](piece, isBlocked, rng.fork(piece.key));
      byKind[kind].push(...trees);
    }
  }
  return {
    orchards: byKind.orchard,
    gardens: byKind.garden,
    cloisters: byKind.cloister,
  };
}

function instancedMesh(
  geometry: BufferGeometry,
  material: Material,
  edgeIds: Float32Array,
  name: string
): InstancedMesh {
  geometry.setAttribute('aInstanceEdgeId', new InstancedBufferAttribute(edgeIds, 1));
  const mesh = new InstancedMesh(geometry, material, edgeIds.length);
  mesh.name = name;
  mesh.frustumCulled = false;
  return mesh;
}

function buildTrunks(
  trees: readonly TreePlacement[],
  colour: LinearRGB,
  edgeIds: Float32Array,
  material: Material,
  seed: number,
  elevation: number | ((p: Vec2) => number)
): InstancedMesh {
  const mesh = instancedMesh(
    buildTrunkTemplate(colour, edgeIds[0]!, seed),
    material,
    edgeIds,
    'tree-trunks'
  );
  const matrix = new Matrix4();
  trees.forEach((tree, i) => {
    const [x, y, z] = layoutToWorld(tree.position, typeof elevation === 'number' ? elevation : elevation(tree.position));
    matrix.makeScale(tree.scale, tree.trunkHeight, tree.scale).setPosition(x, y, z);
    mesh.setMatrixAt(i, matrix);
  });
  mesh.instanceMatrix.needsUpdate = true;
  return mesh;
}

function buildCrowns(
  trees: readonly TreePlacement[],
  variant: number,
  foliage: readonly LinearRGB[],
  edgeIds: Float32Array,
  material: Material,
  rng: Rng,
  elevation: number | ((p: Vec2) => number)
): InstancedMesh {
  const template = buildCrownTemplate(rng.fork(`crown-${variant}`), edgeIds[0]!, rng.next());
  const mesh = instancedMesh(template, material, edgeIds, `tree-crowns-${variant}`);
  const matrix = new Matrix4();
  const colour = new Color();
  trees.forEach((tree, i) => {
    const [x, y, z] = layoutToWorld(
      tree.position,
      (typeof elevation === 'number' ? elevation : elevation(tree.position)) + tree.trunkHeight + CROWN_CENTRE_ABOVE_TRUNK * tree.scale
    );
    matrix.makeScale(tree.scale, tree.scale, tree.scale).setPosition(x, y, z);
    mesh.setMatrixAt(i, matrix);
    mesh.setColorAt(i, colour.setRGB(...foliage[i]!));
  });
  mesh.instanceMatrix.needsUpdate = true;
  mesh.instanceColor!.needsUpdate = true;
  return mesh;
}

/*
 * PLAN §5: trunks in wood and each crown its own treeFoliage centre sample. One trunk mesh for
 * every tree plus one crown mesh per crown variant, so a whole forest costs at most four draws.
 * A tree's trunk and crown share one edge id, starting at firstEdgeIndex.
 */
export function buildTrees(
  trees: readonly TreePlacement[],
  palette: PaletteModel,
  rng: Rng,
  material: Material,
  firstEdgeIndex: number,
  elevation: number | ((p: Vec2) => number) = 0
): TreeMeshes {
  if (trees.length === 0) return { meshes: [], trunks: null, crowns: [], treeCount: 0 };
  const colourRng = rng.fork('foliage');
  const foliage = trees.map((tree) =>
    sampleSwatch(palette.treeFoliage, colourRng.fork(treeName(tree)))
  );
  const edgeIds = Float32Array.from(trees, (_, i) => edgeIdFor(firstEdgeIndex + i));
  const trunkColour = palette.shipHull.median.linear;
  const trunks = buildTrunks(trees, trunkColour, edgeIds, material, rng.next(), elevation);
  const crowns = Array.from({ length: CROWN_VARIANTS }, (_, variant) => variant).flatMap(
    (variant) => {
      const members = trees.flatMap((tree, i) =>
        tree.crownVariant % CROWN_VARIANTS === variant ? [i] : []
      );
      if (members.length === 0) return [];
      const pick = <T>(values: ArrayLike<T>) => members.map((i) => values[i]!);
      const ids = Float32Array.from(pick(edgeIds));
      return [buildCrowns(pick(trees), variant, pick(foliage), ids, material, rng, elevation)];
    }
  );
  return { meshes: [trunks, ...crowns], trunks, crowns, treeCount: trees.length };
}
